import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSkills } from "./hooks/useSkills";
import { useClaudeAgents } from "./hooks/useClaudeAgents";
import { useModels } from "./hooks/useModels";
import SkillsPanel from "./SkillsPanel";
import SkillEditor from "./SkillEditor";
import "./ClaudeAgentChat.css";
import { api } from "./api";
import { useAgentThreads } from "./hooks/useAgentThreads";
import { useAgentChat } from "./hooks/useAgentChat";
import { useEnvironments } from "./hooks/useEnvironments";
import { useEnvironmentFiles } from "./hooks/useEnvironmentFiles";
import { useUserThreadStatus } from "./hooks/useUserThreadStatus";
import AgentThreadsList from "./AgentThreadsList";
import Composer from "./Composer";
import EventList from "./EventList";
import ThreadHeader from "./ThreadHeader";
import EnvironmentSidebar from "./EnvironmentSidebar";
import EnvironmentFilesPanel from "./EnvironmentFilesPanel";
import EnvironmentFileViewer from "./EnvironmentFileViewer";
import ShareEnvironmentModal from "./ShareEnvironmentModal";
import ChatHeader from "./ChatHeader";
import AgentModal from "./AgentModal";
import McpRequiredModal from "./McpRequiredModal";
import Section from "./Section";
import SettingsPanel from "./SettingsPanel";
import McpPanel from "./McpPanel";
import { useMcpServers } from "./hooks/useMcpServers";
import MemoryPanel from "./MemoryPanel";
import { useEnvironmentMemory } from "./hooks/useEnvironmentMemory";
import ScheduledTasksPanel from "./ScheduledTasksPanel";
import { useScheduledTasks } from "./hooks/useScheduledTasks";
import Drawer from "./Drawer";
import DotGridBackground from "./DotGridBackground";
import { IconWarning } from "./Icons";
import { describeError } from "./errors";

const SELECTED_AGENT_STORAGE_KEY = "claudeAgentSelectedAgentId";
const LEFT_COLLAPSED_STORAGE_KEY = "claudeAgentLeftCollapsed";
const RIGHT_COLLAPSED_STORAGE_KEY = "claudeAgentRightCollapsed";
const REQUIRE_APPROVAL_STORAGE_KEY = "claudeAgentRequireApproval";

export default function ClaudeAgentChat({ initialAgentId = null }) {
  // Tell .dashboard-main to render edge-to-edge while the workspace is mounted.
  // Belt-and-braces with the :has(.ca-chat) selector for older browsers.
  useEffect(() => {
    const host = document.querySelector(".dashboard-main");
    if (!host) return;
    host.classList.add("ca-chat-host");
    return () => host.classList.remove("ca-chat-host");
  }, []);

  const { agents, loading: agentsLoading, refresh: refreshAgents } = useClaudeAgents();
  const { models, selectedModel, setSelectedModel } = useModels();
  const agentMap = useMemo(
    () => Object.fromEntries(agents.map((a) => [a._id, a])),
    [agents]
  );

  const { user, isAdmin } = useAuth() || {};
  const navigate = useNavigate();
  const userInitials = useMemo(() => {
    const src = user?.name || user?.email || "";
    if (!src) return "?";
    const parts = src.split(/[@\s.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return src.slice(0, 2).toUpperCase();
  }, [user]);

  // Selected agent: URL > localStorage > first available.
  const [activeAgentId, setActiveAgentIdState] = useState(() => {
    if (initialAgentId) return initialAgentId;
    try { return localStorage.getItem(SELECTED_AGENT_STORAGE_KEY) || null; }
    catch (_) { return null; }
  });
  const setActiveAgentId = useCallback((id) => {
    setActiveAgentIdState(id);
    try {
      if (id) localStorage.setItem(SELECTED_AGENT_STORAGE_KEY, id);
      else localStorage.removeItem(SELECTED_AGENT_STORAGE_KEY);
    } catch (_) {}
  }, []);
  // Auto-pick the first agent once loaded if none chosen yet. If a previously-
  // selected agent has disappeared (soft-deleted by an admin), swap to the
  // next available one and surface a banner so the user knows.
  useEffect(() => {
    if (agentsLoading) return;
    if (!activeAgentId && agents.length > 0) {
      setActiveAgentId(agents[0]._id);
    } else if (activeAgentId && !agents.some((a) => a._id === activeAgentId)) {
      const next = agents[0];
      setActiveAgentId(next?._id || null);
      if (next) {
        setError(`The previously selected agent is no longer available — switched to "${next.name}".`);
      }
    }
  }, [agentsLoading, agents, activeAgentId, setActiveAgentId]);

  // Create/edit user agents right from the picker. null | {} (new) | full doc (edit).
  const [agentModal, setAgentModal] = useState(null);
  const openNewAgent = useCallback(() => setAgentModal({}), []);
  const openEditAgent = useCallback(async () => {
    try {
      setAgentModal(await api.getAgent(activeAgentId));
    } catch (e) {
      setError(describeError(e) || "Couldn't load the agent's settings.");
    }
  }, [activeAgentId]);
  const saveAgent = useCallback(async (payload) => {
    const saved = agentModal?._id
      ? await api.updateAgent(agentModal._id, payload)
      : await api.createAgent(payload);
    setAgentModal(null);
    await refreshAgents();
    setActiveAgentId(saved._id);
  }, [agentModal, refreshAgents, setActiveAgentId]);
  const removeAgent = useCallback(async () => {
    await api.deleteAgent(agentModal._id);
    setAgentModal(null);
    await refreshAgents(); // the auto-pick effect swaps to the next agent
  }, [agentModal, refreshAgents]);

  const {
    envs, activeEnvId, setActiveEnvId,
    createEnvironment, renameEnvironment, deleteEnvironment, shareEnvironment,
  } = useEnvironments();
  const {
    threads, refresh, createThread, deleteThread, renameThread,
  } = useAgentThreads(activeAgentId, activeEnvId);
  const {
    files: envFiles, loading: filesLoading, refresh: refreshFiles,
    readFile, writeFile, deleteFile,
  } = useEnvironmentFiles(activeEnvId);
  const {
    skills, activeCount: skillsActiveCount, loading: skillsLoading,
    getSkill, createSkill, uploadSkillZip, patchSkill, toggleSkill, deleteSkill,
  } = useSkills();

  // Bridge refs: useMcpServers runs before activeThreadId / useAgentChat below,
  // so connect reads the open chat id and the chat's reconnect through refs
  // synced further down (avoids reordering the hooks).
  const activeThreadIdRef = useRef(null);
  const chatReconnectRef = useRef(null);
  // Stable so useMcpServers.connect isn't rebuilt every render.
  const onMcpRebound = useCallback(() => chatReconnectRef.current?.(), []);

  const {
    servers: mcpServers, connectedCount: mcpConnectedCount, needsReauthCount: mcpNeedsReauthCount, loading: mcpLoading,
    connect: connectMcp, reauthenticate: reauthMcp, disconnect: disconnectMcp,
    setEnabled: setMcpEnabled,
  } = useMcpServers(activeEnvId, {
    threadIdRef: activeThreadIdRef,
    onRebound: onMcpRebound,
  });

  const {
    memories, loading: memoryLoading, error: memoryError, refresh: refreshMemory,
  } = useEnvironmentMemory(activeEnvId);

  const {
    tasks: scheduledTasks, category: scheduledCategory,
    setCategory: setScheduledCategory,
    loading: scheduledLoading, error: scheduledError,
    createTask: createScheduledTask, updateTask: updateScheduledTask,
    deleteTask: deleteScheduledTask, runTaskNow: runScheduledTaskNow,
  } = useScheduledTasks(activeEnvId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState(null);
  const [shareModalEnvId, setShareModalEnvId] = useState(null);
  const [openFilePath, setOpenFilePath] = useState(null);
  const [pendingFirstMessage, setPendingFirstMessage] = useState(null);
  // The first message of a brand-new chat, shown instantly while the thread is
  // being created so the send feels snappy. Cleared once the live stream has
  // events (EventList takes over) or the send fails.
  const [optimisticFirst, setOptimisticFirst] = useState(null);

  // Close the file viewer when switching environments.
  useEffect(() => { setOpenFilePath(null); }, [activeEnvId]);

  // Pre-start the environment's sandbox the moment it's opened, hiding the
  // container cold-start behind navigation (no-op on the cloud path).
  useEffect(() => {
    if (activeEnvId) api.warmEnvironment(activeEnvId);
  }, [activeEnvId]);

  const [leftCollapsed, setLeftCollapsed] = useState(() => {
    try { return localStorage.getItem(LEFT_COLLAPSED_STORAGE_KEY) === "1"; }
    catch (_) { return false; }
  });
  const [rightCollapsed, setRightCollapsed] = useState(() => {
    try { return localStorage.getItem(RIGHT_COLLAPSED_STORAGE_KEY) === "1"; }
    catch (_) { return false; }
  });
  const toggleLeft = useCallback(() => {
    setLeftCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(LEFT_COLLAPSED_STORAGE_KEY, next ? "1" : "0"); } catch (_) {}
      return next;
    });
  }, []);
  const toggleRight = useCallback(() => {
    setRightCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(RIGHT_COLLAPSED_STORAGE_KEY, next ? "1" : "0"); } catch (_) {}
      return next;
    });
  }, []);

  // "Require approval before running tools" — when on, new chats are created
  // against an always_ask variant of the default agent so each tool use waits
  // for an allow/deny. Applies to new chats only (an agent's policy is fixed at
  // thread-creation); persisted per browser.
  const [requireApproval, setRequireApprovalState] = useState(() => {
    try { return localStorage.getItem(REQUIRE_APPROVAL_STORAGE_KEY) === "1"; }
    catch (_) { return false; }
  });
  const setRequireApproval = useCallback((next) => {
    setRequireApprovalState(next);
    try { localStorage.setItem(REQUIRE_APPROVAL_STORAGE_KEY, next ? "1" : "0"); } catch (_) {}
  }, []);

  const openNewSkill = () => { setEditingSkillId(null); setEditorOpen(true); };
  const openEditSkill = (skillId) => { setEditingSkillId(skillId); setEditorOpen(true); };

  const [activeThreadId, setActiveThreadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  // Usage block: set from /usage/limit on mount so the notice is up before the
  // user types, and re-checked whenever a send is refused so it appears without
  // a reload. `message` comes from the server — the client never computes it.
  const [usageBlock, setUsageBlock] = useState(null);
  const { state, sendMessage, interrupt, reconnect } = useAgentChat(activeThreadId);

  const checkUsageLimit = useCallback(async () => {
    try {
      const r = await api.usageLimit();
      setUsageBlock(r?.blocked ? r.message : null);
      return !!r?.blocked;
    } catch (_) {
      // A failed check must not lock anyone out — the server is the enforcer.
      return false;
    }
  }, []);
  useEffect(() => { checkUsageLimit(); }, [checkUsageLimit]);
  // Keep the bridge refs (read by useMcpServers.connect) pointed at the latest
  // open chat + its reconnect, so an eager MCP rebind reconnects the right chat.
  useEffect(() => { activeThreadIdRef.current = activeThreadId; }, [activeThreadId]);
  useEffect(() => { chatReconnectRef.current = reconnect; }, [reconnect]);

  // Reset selected thread when env changes.
  useEffect(() => {
    setActiveThreadId(null);
    setThread(null);
    setError(null);
    setOptimisticFirst(null);
    setPendingFirstMessage(null);
  }, [activeEnvId]);

  // When user picks a thread, sync agent selection to that thread's agent.
  useEffect(() => {
    if (!activeThreadId) { setThread(null); return; }
    api.getThread(activeThreadId).then((t) => {
      setThread(t);
      setError(null);
      // Sync the picker to the thread's agent — but only if that agent is one
      // the picker can actually show. Threads created with "require approval"
      // use a hidden always_ask variant; syncing to it would blank the picker,
      // so we leave the canonical default selected instead.
      if (t && t.agent_id && t.agent_id !== activeAgentId && agentMap[t.agent_id]) {
        setActiveAgentId(t.agent_id);
      }
    }).catch((err) => {
      // Only clear the thread on "not found" — for transient errors keep
      // showing the previously-loaded thread (if any) so the user doesn't
      // see a blank screen mid-session.
      if (err && err.status === 404) {
        setThread(null);
        setActiveThreadId(null);
        setError("This chat no longer exists.");
      } else {
        console.warn("getThread failed:", err);
        setError("Couldn't load chat — check your connection and refresh to try again.");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId]);

  useUserThreadStatus(useCallback(() => {
    refresh();
    refreshFiles();
  }, [refresh, refreshFiles]));

  // While the agent is working, poll environment files in the background so
  // newly-created files show up promptly. Refreshes are silent (no loading
  // flash) thanks to useEnvironmentFiles' background-refresh behavior.
  useEffect(() => {
    if (state.status !== "running") return undefined;
    const id = setInterval(() => { refreshFiles(); }, 4000);
    return () => clearInterval(id);
  }, [state.status, refreshFiles]);

  // The while-running poll can miss a file created in the agent's LAST tool call
  // (the turn then goes idle and the poll stops), so refresh once whenever the
  // session settles to idle. In self-hosted mode the session also dips to idle
  // between tool calls — those extra silent refreshes are harmless and make new
  // files appear promptly.
  useEffect(() => {
    if (state.status === "idle") refreshFiles();
  }, [state.status, refreshFiles]);

  const activeEnv = envs.find((e) => e._id === activeEnvId);
  const noAgents = !agentsLoading && agents.length === 0;
  // The model picker works on the system-default agent and on user-created
  // agents (the backend derives a per-(agent, model) variant). Admin-defined
  // agents come with their model baked in.
  const activeAgent = agentMap[activeAgentId];
  const modelPickerEnabled = !activeAgent || !!activeAgent.is_system_default
    || !!activeAgent.is_user_agent;
  // User agents start from their own model (or the admin default when unset);
  // a pick while on one is a per-agent override for this visit, not the saved
  // preference, so switching back to "Claude" keeps the user's usual model.
  const [agentModelOverride, setAgentModelOverride] = useState({});
  const catalogDefault = models.find((m) => m.is_default)?.id || models[0]?.id || null;
  const effectiveModel = activeAgent?.is_user_agent
    ? (agentModelOverride[activeAgentId] || activeAgent.model || catalogDefault)
    : selectedModel;
  const onSelectModel = useCallback((id) => {
    if (activeAgent?.is_user_agent) {
      setAgentModelOverride((o) => ({ ...o, [activeAgentId]: id }));
    } else {
      setSelectedModel(id);
    }
  }, [activeAgent, activeAgentId, setSelectedModel]);

  // Shared by the new-chat path and the normal send: a failed send must drop
  // the standalone first-message preview (or the header stays "initializing…"
  // with a working indicator forever) and re-check the usage block on a 429.
  const handleSendError = useCallback(async (e) => {
    setOptimisticFirst(null);
    // A 429 may be the monthly usage block — re-check so the banner and the
    // disabled composer appear instead of a one-off error the user can retry
    // past. Any other refusal (daily caps, concurrency) leaves it untouched.
    if (e?.status === 429) {
      const blocked = await checkUsageLimit();
      if (blocked) return;
    }
    setError(describeError(e) || "failed to send");
  }, [checkUsageLimit]);

  // First-message gate: a user agent with pinned connectors that aren't
  // connected (or whose auth lapsed) opens a connect prompt instead of sending.
  // `{ missing, payload, resolve }` while open; payload is replayed on proceed
  // and `resolve` settles the Composer's await (true = sent, false = cancelled).
  const [mcpGate, setMcpGate] = useState(null);

  const onSend = useCallback(async ({ text, attachedPaths = [], attachments = [] }, { skipGate = false } = {}) => {
    try {
      if (!activeThreadId && !skipGate) {
        const required = agentMap[activeAgentId]?.mcp_server_urls || [];
        // Servers missing from the catalog can't be connected at all (thread
        // creation skips them too), so only gate on ones the user could fix.
        const missing = required
          .map((url) => mcpServers.find((s) => s.url === url))
          .filter((s) => s && (!s.connected || s.needs_reauth))
          .map((s) => ({ name: s.name, url: s.url }));
        if (missing.length) {
          return new Promise((resolve) => setMcpGate({
            missing, payload: { text, attachedPaths, attachments }, resolve,
          }));
        }
      }
      if (!activeThreadId) {
        // Render the user's message immediately, before the (slow) createThread.
        // The shared idempotencyKey lets the same bubble carry through to the
        // hook's optimistic placeholder without remounting (no flicker).
        const idempotencyKey = `user-${crypto.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random()}`;
        setOptimisticFirst({ text, attachedPaths, attachments, idempotencyKey });
        // New chat: create the thread, then defer the first send until the
        // chat hook is bound to the new thread id (see the effect below) so it
        // goes through the same optimistic-render + WS-reconcile path as every
        // other message. Pass the picker's selection through; backend only
        // honors it for system-default agents.
        const t = await createThread({
          model: modelPickerEnabled ? effectiveModel : null,
          requireApproval,
        });
        // Resolve the Composer's await only once the deferred send below has
        // actually gone out, so a failed first message restores the draft
        // (false) exactly like a failed follow-up does.
        return new Promise((resolve) => {
          setPendingFirstMessage({ threadId: t.thread_id, text, attachedPaths, attachments, idempotencyKey, resolve });
          setActiveThreadId(t.thread_id);
          refresh();
        });
      }
      await sendMessage({ text, attachedPaths, attachments });
      await refresh();
    } catch (e) {
      await handleSendError(e);
      return false; // tells the Composer to restore the draft
    }
  }, [activeThreadId, createThread, sendMessage, refresh, modelPickerEnabled,
      effectiveModel, requireApproval, handleSendError, agentMap, activeAgentId, mcpServers]);

  const proceedGated = useCallback(() => {
    const gate = mcpGate;
    setMcpGate(null);
    if (!gate) return;
    // Adopt the real send's outcome so a failure after "Connect & send"
    // restores the draft instead of silently eating it.
    gate.resolve(onSend(gate.payload, { skipGate: true }));
  }, [mcpGate, onSend]);
  const cancelGated = useCallback(() => {
    mcpGate?.resolve(false);
    setMcpGate(null);
  }, [mcpGate]);

  // Drop the standalone preview only once the stream is genuinely LIVE — the
  // empty new-thread replay has finished AND the agent is actually running (or
  // has produced an event). Holding `initializing` until then keeps the working
  // indicator continuous and the replay banner suppressed, so the user only
  // sees the header status change — never a flash of the message reappearing or
  // the indicator blinking with a new word.
  const streamLive =
    !state.replaying &&
    (state.status === "running" ||
      state.events.some((e) => (e.type || "").startsWith("agent.")));
  useEffect(() => {
    if (optimisticFirst && streamLive) setOptimisticFirst(null);
  }, [optimisticFirst, streamLive]);

  // `initializing` = a first message is in flight for a brand-new chat (from
  // send until the stream settles). EventList uses it to keep the working
  // indicator continuous and to ignore the empty new-thread replay, so the only
  // thing the user sees change is the status in the header.
  const initializing = !!optimisticFirst;
  // While we have no real events yet, feed EventList a synthetic placeholder so
  // the same single component renders throughout — no container swap, no
  // flicker. The shared idempotencyKey keeps the bubble's React key stable
  // (seq 0.5) through the hand-off to the hook's optimistic placeholder.
  const showSynthetic = initializing && state.events.length === 0;
  // In-flight agent.message previews render as live messages at the tail of
  // the timeline; the buffered message takes over under the same event_id.
  const previewEvents = useMemo(
    () => Object.entries(state.previews || {}).map(([id, text]) => ({
      seq: Infinity, type: "agent.message", event_id: id, thread_id: null,
      payload: { text }, processed_at: null, _live: true, _preview: true,
    })),
    [state.previews],
  );
  const displayEvents = showSynthetic
    ? [{
        seq: 0.5,
        type: "user.message",
        event_id: optimisticFirst.idempotencyKey,
        thread_id: null,
        payload: { text: optimisticFirst.text, attachments: optimisticFirst.attachments || [] },
        _optimistic: true,
      }]
    : (previewEvents.length ? [...state.events, ...previewEvents] : state.events);

  // When the agent is blocked on tool approvals (always_ask), the idle event's
  // stop_reason is `requires_action` with the blocked event ids, which map to
  // agent.tool_use / agent.mcp_tool_use events awaiting an allow/deny.
  //
  // BUT requires_action also fires for tools that are NOT permission-gated: in
  // self-hosted mode the session transiently enters requires_action for EVERY
  // built-in tool call while our fleet worker posts the tool result. Those are
  // auto-allowed (evaluated_permission !== "ask") and must NOT show allow/deny
  // (clicking them 400s — there's no pending permission request). So gate the
  // confirmation UI on evaluated_permission === "ask".
  const pendingConfirmationIds = useMemo(() => {
    const sr = state.stopReason;
    // (The reducer reports a requires_action pause as "running", so key off the
    // stop reason alone — it's cleared by the next status event.)
    if (!sr || typeof sr !== "object" || sr.type !== "requires_action") {
      return null;
    }
    const blocked = new Set(sr.event_ids || []);
    if (blocked.size === 0) return null;
    const askIds = new Set();
    for (const ev of state.events) {
      if (!blocked.has(ev.event_id)) continue;
      if ((ev.type === "agent.tool_use" || ev.type === "agent.mcp_tool_use")
          && ev.payload && ev.payload.evaluated_permission === "ask") {
        askIds.add(ev.event_id);
      }
    }
    return askIds.size > 0 ? askIds : null;
  }, [state.stopReason, state.events]);

  const onConfirmTool = useCallback(async (toolUseId, result, denyMessage) => {
    if (!activeThreadId) return;
    try {
      await api.confirmTool(activeThreadId, { toolUseId, result, denyMessage });
      await refresh();
    } catch (e) {
      setError(describeError(e) || "failed to send tool decision");
      throw e; // lets the tool card restore its Approve/Deny buttons
    }
  }, [activeThreadId, refresh]);

  // Send the first message of a freshly-created chat once the chat hook has
  // re-bound `sendMessage` to the new thread id. Going through the hook gives
  // us the optimistic placeholder immediately and reconciles it over the WS.
  useEffect(() => {
    if (!pendingFirstMessage) return;
    if (pendingFirstMessage.threadId !== activeThreadId) return;
    const { text, attachedPaths, attachments, idempotencyKey, resolve } = pendingFirstMessage;
    setPendingFirstMessage(null);
    sendMessage({ text, attachedPaths, attachments, idempotencyKey })
      .then(() => { refresh(); resolve?.(true); })
      .catch(async (e) => { await handleSendError(e); resolve?.(false); });
  }, [pendingFirstMessage, activeThreadId, sendMessage, refresh, handleSendError]);

  // Welcome screen (no conversation) → shader at full opacity; once messages
  // show, dim it to 25% so the transcript stays readable.
  const showingMessages = !!(optimisticFirst || activeThreadId || state.replaying);

  return (
    <div className={`ca-chat${leftCollapsed ? " left-collapsed" : ""}${rightCollapsed ? " right-collapsed" : ""}${showingMessages ? " ca-bg-dim" : ""}`}>
      <DotGridBackground />
      {/* LEFT: threads in current env */}
      <aside className="ca-sidebar ca-sidebar-left">
        <div className="ca-sidebar-title">
          Chats {activeEnv && <span className="ca-sidebar-title-sub">in {activeEnv.name}</span>}
        </div>
        <AgentThreadsList
          threads={threads}
          activeThreadId={activeThreadId}
          agentMap={agentMap}
          newChatDisabled={!activeAgentId}
          onPick={setActiveThreadId}
          onNewChat={() => {
            // Instant: drop into a fresh empty chat. The thread is created
            // lazily on the first send (see onSend) — no slow create_session
            // round-trip here, and no empty thread cluttering the list.
            setError(null);
            setOptimisticFirst(null);
            setPendingFirstMessage(null);
            setActiveThreadId(null);
          }}
          onRename={renameThread}
          onDelete={(id) => {
            // Clear the view instantly when deleting the open chat; the row is
            // removed optimistically inside deleteThread.
            if (activeThreadId === id) {
              setActiveThreadId(null);
              setThread(null);
            }
            return deleteThread(id);
          }}
        />
      </aside>

      {/* CENTER: chat */}
      <main className="ca-main">
        <ChatHeader
          agents={agents}
          activeAgentId={activeAgentId}
          onAgentChange={setActiveAgentId}
          onNewAgent={openNewAgent}
          onEditAgent={openEditAgent}
          canEditActive={!!agentMap[activeAgentId]?.is_owner}
          activeEnvName={activeEnv?.name}
          leftCollapsed={leftCollapsed}
          rightCollapsed={rightCollapsed}
          onToggleLeft={toggleLeft}
          onToggleRight={toggleRight}
        />
        {(thread || optimisticFirst) && (
          <ThreadHeader
            thread={thread || { title: "New chat" }}
            status={optimisticFirst && state.status !== "running" ? "initializing" : state.status}
            stopReason={state.stopReason}
          />
        )}
        {usageBlock && <div className="ca-usage-block">{usageBlock}</div>}
        {error && <div className="ca-error">{error}</div>}
        {optimisticFirst || activeThreadId || state.replaying ? (
          <EventList
            events={displayEvents}
            replaying={state.replaying}
            loading={!!activeThreadId && !state.loaded && !initializing}
            status={state.status}
            initializing={initializing}
            pendingConfirmationIds={pendingConfirmationIds}
            onConfirmTool={onConfirmTool}
          />
        ) : (
          <div className="ca-welcome">
            <div className="ca-welcome-inner">
              {noAgents ? (
                <>
                  <h2>Chat is unavailable</h2>
                  <p className="ca-welcome-warning">
                    <IconWarning size={14} />
                    The default agent couldn't be initialized. Check the Anthropic API key on the server.
                  </p>
                </>
              ) : (
                <>
                  <h2>You're in {activeEnv?.name || "Personal"}</h2>
                  <p>
                    Type a message below to start a new chat. Your active skills load automatically.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        <Composer
          onSend={onSend}
          status={state.status}
          onStop={async () => {
            try {
              await interrupt();
            } catch (e) {
              setError(describeError(e) || "Couldn't stop the agent — try again.");
            }
          }}
          environmentId={activeEnvId}
          onUploaded={refreshFiles}
          disabled={!!usageBlock || noAgents || state.status === "terminated"
            || (!activeThreadId && state.status === "running")}
          disabledHint={usageBlock ? "Messaging paused — see the notice above."
            : state.status === "terminated" ? "This chat has ended — start a new chat to continue."
            : null}
          models={models}
          selectedModel={effectiveModel}
          onSelectModel={onSelectModel}
          modelPickerEnabled={modelPickerEnabled}
        />
      </main>

      {/* RIGHT: floating section stack — Environments, Skills, Files */}
      <div className="ca-right">
        <Section title="Environments" defaultOpen>
          <EnvironmentSidebar
            envs={envs}
            activeEnvId={activeEnvId}
            onPick={setActiveEnvId}
            onCreate={createEnvironment}
            onRename={renameEnvironment}
            onDelete={deleteEnvironment}
            onShare={(envId) => setShareModalEnvId(envId)}
          />
        </Section>

        <Section title="Skills" count={skillsActiveCount}>
          <SkillsPanel
            skills={skills}
            loading={skillsLoading}
            activeCount={skillsActiveCount}
            isAdmin={isAdmin}
            onNew={openNewSkill}
            onEdit={openEditSkill}
            onToggle={toggleSkill}
            onDelete={deleteSkill}
            onUploadZip={uploadSkillZip}
            lockedSkillIds={activeThreadId ? (thread?.skill_ids ?? []) : null}
          />
        </Section>

        <Section title="Connectors" count={mcpConnectedCount} warn={mcpNeedsReauthCount > 0}>
          <McpPanel
            servers={mcpServers}
            loading={mcpLoading}
            onConnect={connectMcp}
            onReauth={reauthMcp}
            onDisconnect={disconnectMcp}
            onToggle={setMcpEnabled}
          />
        </Section>

        <Section title="Scheduled Tasks" count={scheduledTasks.length}>
          <ScheduledTasksPanel
            tasks={scheduledTasks}
            category={scheduledCategory}
            onCategoryChange={setScheduledCategory}
            isTopManager={user?.role === "Top Manager"}
            mcpServers={mcpServers}
            onConnectMcp={connectMcp}
            loading={scheduledLoading}
            error={scheduledError}
            envId={activeEnvId}
            agents={agents}
            models={models}
            onCreate={createScheduledTask}
            onUpdate={updateScheduledTask}
            onDelete={deleteScheduledTask}
            onRunNow={runScheduledTaskNow}
            onOpenThread={setActiveThreadId}
          />
        </Section>

        <Section title="Memory" count={memories.length}>
          <MemoryPanel
            memories={memories}
            loading={memoryLoading}
            error={memoryError}
            onRefresh={refreshMemory}
          />
        </Section>

        <Section title="Files" count={envFiles.length}>
          <EnvironmentFilesPanel
            files={envFiles}
            loading={filesLoading}
            envId={activeEnvId}
            envName={activeEnv?.name || ""}
            selectedPath={openFilePath}
            onOpenFile={setOpenFilePath}
            onWrite={writeFile}
            onDelete={deleteFile}
            onUploadDone={refreshFiles}
          />
        </Section>

        <Section title="Settings">
          <SettingsPanel
            requireApproval={requireApproval}
            onRequireApprovalChange={setRequireApproval}
          />
        </Section>

        {isAdmin && (
          <Section title="Admin">
            <div className="ca-admin-card">
              <p className="ca-admin-card-desc">
                Usage, cost, and activity across all users.
              </p>
              <button
                type="button"
                className="ca-btn ca-btn--primary ca-btn--sm"
                onClick={() => navigate("/claude-agent/admin")}
              >
                View dashboard
              </button>
            </div>
          </Section>
        )}
      </div>

      <Drawer
        open={!!openFilePath}
        title={openFilePath ? `File — ${openFilePath}` : "File"}
        onClose={() => setOpenFilePath(null)}
        width={720}
      >
        {openFilePath && (
          <EnvironmentFileViewer
            path={openFilePath}
            envId={activeEnvId}
            onRead={readFile}
            onWrite={writeFile}
            onDelete={deleteFile}
            onChanged={refreshFiles}
            onClose={() => setOpenFilePath(null)}
          />
        )}
      </Drawer>

      <SkillEditor
        open={editorOpen}
        skillId={editingSkillId}
        isAdmin={isAdmin}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { /* useSkills.refresh runs inside the hook */ }}
        getSkill={getSkill}
        createSkill={createSkill}
        patchSkill={patchSkill}
      />
      <ShareEnvironmentModal
        open={!!shareModalEnvId}
        envId={shareModalEnvId}
        ownerEmail={envs.find((e) => e._id === shareModalEnvId)?.owner_email}
        onClose={() => setShareModalEnvId(null)}
        onSaved={() => { /* useEnvironments.refresh runs inside shareEnvironment */ }}
        shareEnvironment={shareEnvironment}
      />
      {mcpGate && (
        <McpRequiredModal
          missing={mcpGate.missing}
          servers={mcpServers}
          title="Connect this agent's connectors"
          hint="This agent uses the following connectors. Connect them and your message will send automatically."
          actionLabel="Send message"
          autoProceed
          onConnect={connectMcp}
          onRetry={proceedGated}
          onContinueAnyway={proceedGated}
          onClose={cancelGated}
        />
      )}
      {agentModal !== null && (
        <AgentModal
          agent={agentModal._id ? agentModal : null}
          models={models}
          skills={skills}
          mcpServers={mcpServers}
          onClose={() => setAgentModal(null)}
          onSave={saveAgent}
          onDelete={agentModal._id ? removeAgent : undefined}
        />
      )}
    </div>
  );
}
